import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import PieChart from './PieChart';
import MeanChart from './MeanChart';
import measurementService from '../../../../services/measurementService';
import patientService from '../../../../services/patientService';
import { checkBGValue, getPresetDateRangeParams } from '../../../../utils/measurementUtils';
import { PATIENT_SCHEDULE_TARGET_PREF_EVENT } from '../../../../utils/patientScheduleTargetPreference';
import { toast } from 'react-toastify';
import EmailModal from '../../../../components/EmailModal/EmailModal';
import emailService from '../../../../services/emailService';
import { exportElementToPdf, printElement } from '../../../../utils/reportExportUtils';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import MeasurementChartHeader from '../../ChartHeader/MeasurementChartHeader';
import './DayChart.css';

const DayChart = ({ practiceId: propPracticeId, patientId: propPatientId }) => {
  const params = useParams();
  const practiceId = propPracticeId || params.practiceId;
  const patientId = propPatientId || params.patientId;

  const [queryType, setQueryType] = useState('dateRange');
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const cacheKey = `chart_patient_meta_${practiceId}_${patientId}`;
  const [patientData, setPatientData] = useState(() => {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [measurements, setMeasurements] = useState([]);
  const [scheduleTargets, setScheduleTargets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queryLoading, setQueryLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const { updateBreadcrumbs } = useBreadcrumb();

  // Fetch patient data and schedule targets on mount
  useEffect(() => {
    if (practiceId && patientId) {
      setQueryType('dateRange');
      setDateRange('Last 30 days');
      setFromDate('');
      setToDate('');
      fetchPatientData();
      fetchScheduleTargets();
    }
  }, [practiceId, patientId]);

  useEffect(() => {
    if (!practiceId || !patientId) return undefined;
    const onPref = (ev) => {
      const d = ev?.detail;
      if (String(d?.practiceId) !== String(practiceId) || String(d?.patientId) !== String(patientId)) return;
      fetchScheduleTargets();
    };
    window.addEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
    return () => window.removeEventListener(PATIENT_SCHEDULE_TARGET_PREF_EVENT, onPref);
  }, [practiceId, patientId]);

  // Fetch measurements when query parameters change
  useEffect(() => {
    if (practiceId && patientId) {
      fetchMeasurements();
    }
  }, [practiceId, patientId, dateRange, fromDate, toDate]);

  // Breadcrumbs effect
  useEffect(() => {
    if (patientData?.patient && practiceId && patientId) {
      updateBreadcrumbs([
        { label: 'Practices', path: '/practices' },
        { label: patientData.patient.practice_name || 'Practice', path: `/practices/${practiceId}` },
        { label: 'Patients', path: `/practices/${practiceId}/patients` },
        { 
          label: `${patientData.patient.last_name || ''}, ${patientData.patient.first_name || ''}`.replace(/^, | ,$|(, )+/g, '$1').trim() || 'Patient', 
          path: `/practices/${practiceId}/patients/${patientId}` 
        },
        { label: 'Blood Glucose Chart', path: null, active: true }
      ]);
    }
  }, [patientData, practiceId, patientId, updateBreadcrumbs]);

  const fetchPatientData = async () => {
    try {
      const response = await patientService.getMeasurementHeaderFast(practiceId, patientId);
      if (response.data.success) {
        setPatientData(response.data.data);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(response.data.data));
        } catch {
          // ignore cache write errors
        }
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
      toast.error('Failed to load patient data');
    }
  };

  const fetchScheduleTargets = async () => {
    try {
      const data = await patientService.getResolvedScheduleTargetsForPatient(practiceId, patientId);
      setScheduleTargets(data);
    } catch (error) {
      console.error('Error fetching schedule targets:', error);
    }
  };

  const fetchMeasurements = async () => {
    try {
      setQueryLoading(true);
      let fromDateParam, toDateParam;

      if (queryType === 'dateRange') {
        const params = getPresetDateRangeParams(dateRange);
        fromDateParam = params.fromDateParam;
        toDateParam = params.toDateParam;
      } else {
        if (fromDate && toDate) {
          if (fromDate.includes('/')) {
            const [fromMonth, fromDay, fromYear] = fromDate.split('/');
            const [toMonth, toDay, toYear] = toDate.split('/');
            fromDateParam = `${fromYear}-${fromMonth.padStart(2, '0')}-${fromDay.padStart(2, '0')}`;
            toDateParam = `${toYear}-${toMonth.padStart(2, '0')}-${toDay.padStart(2, '0')}`;
          } else {
            fromDateParam = fromDate;
            toDateParam = toDate;
          }
        }
      }

      if (fromDateParam && toDateParam) {
        const response = await measurementService.getBloodGlucoseByDateRange(
          practiceId,
          patientId,
          fromDateParam,
          toDateParam,
          'daily_average'
        );

        if (response.data?.success) {
          setMeasurements(response.data.data || []);
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching measurements:', error);
      if (error.response?.status === 404) {
        console.log('Measurement API endpoint not yet available');
        setMeasurements([]);
      } else {
        toast.error('Failed to load measurements');
        setMeasurements([]);
      }
    } finally {
      setQueryLoading(false);
      setLoading(false);
    }
  };

  const handleQuery = () => {
    if (queryType === 'from' && (!fromDate || !toDate)) {
      toast.warning('Please select both start and end dates');
      return;
    }
    fetchMeasurements();
  };

  const patientName = patientData?.patient 
    ? `${patientData.patient.first_name || ''} ${patientData.patient.last_name || ''}`.trim()
    : 'Loading...';
  const patientDOB = patientData?.patient?.date_of_birth 
    ? new Date(patientData.patient.date_of_birth).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : 'Loading...';
  const patientVitals = patientData?.patient?.vitals || '';

  // Get target for a period
  const getTargetForPeriod = (periodName) => {
    if (!scheduleTargets?.bloodGlucoseTargetRange) return { min: 60, max: 110 };
    const targets = scheduleTargets.bloodGlucoseTargetRange;
    
    // Handle Before-Meal and After-Meal targets
    if (periodName === 'Before-Meal') {
      const target = targets.find(t => 
        t.name?.includes('Before-Meal') || t.name?.includes('Before Meal')
      ) || targets[0] || { min: 60, max: 110 };
      return {
        min: target.minimum || target.min || 60,
        max: target.maximum || target.max || 110
      };
    }
    
    if (periodName === 'After-Meal') {
      const target = targets.find(t => 
        t.name?.includes('After-Meal') || t.name?.includes('After Meal')
      ) || targets[0] || { min: 70, max: 140 };
      return {
        min: target.minimum || target.min || 70,
        max: target.maximum || target.max || 140
      };
    }
    
    // Find target by period name or use first as default
    const period = scheduleTargets.bloodGlucoseTimePeriod?.find(p => p.name === periodName);
    const target = targets.find(
      t => t.name?.includes(periodName) || t.periodName === periodName || t.id === period?.id
    ) || targets[0] || { min: 60, max: 110 };
    
    return {
      min: target.minimum || target.min || 60,
      max: target.maximum || target.max || 110
    };
  };

  // Calculate chart data - show all individual measurements by time of day
  const chartData = useMemo(() => {
    if (!measurements || measurements.length === 0) return { dataPoints: [] };

    // Create data points for all measurements
    const dataPoints = [];
    
    measurements.forEach(m => {
      const bgValue = parseFloat(m.blood_glucose_value_1 || m.blood_glucose_value);
      if (isNaN(bgValue)) return;
      
      const measureTime = m.measurement_time || 
                         m.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
                         m.measure_date_time?.split(' ')[1]?.slice(0, 5) ||
                         '';
      
      if (!measureTime) return;
      
      const [hours, minutes] = measureTime.split(':').map(Number);
      if (isNaN(hours) || isNaN(minutes)) return;
      
      const timeInMinutes = hours * 60 + minutes;
      const period = m.period_name || m.period || m.measure_note || m.note || '';
      
      // Determine if before or after meal for target
      let targetPeriod = 'Before-Meal';
      if (period.toLowerCase().includes('after')) {
        targetPeriod = 'After-Meal';
      }
      
      const target = getTargetForPeriod(targetPeriod);
      const status = checkBGValue(bgValue, target);
      
      dataPoints.push({
        timeInMinutes,
        value: bgValue,
        status,
        date: m.measurement_date || m.measure_new_date_time?.split('T')[0] || m.created_at?.split('T')[0],
        time: measureTime,
        period
      });
    });

    // Sort by time
    dataPoints.sort((a, b) => a.timeInMinutes - b.timeInMinutes);

    return { dataPoints };
  }, [measurements, scheduleTargets]);

  // Chart dimensions and scaling
  const chartWidth = 800;
  const chartHeight = 250;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const plotWidth = chartWidth - padding.left - padding.right;
  const plotHeight = chartHeight - padding.top - padding.bottom;
  const maxY = 400; // Maximum BG value in mg/dL
  const minY = 0;
  const maxTime = 24 * 60; // 24 hours in minutes

  // Scale functions
  const scaleX = (timeInMinutes) => {
    return padding.left + (timeInMinutes / maxTime) * plotWidth;
  };

  const scaleY = (value) => {
    if (value == null) return null;
    return padding.top + plotHeight - ((value - minY) / (maxY - minY)) * plotHeight;
  };

  // Format time for display
  const formatTime = (timeInMinutes) => {
    const hours = Math.floor(timeInMinutes / 60);
    const minutes = timeInMinutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;
    return `${displayHour}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  // Get color based on status
  const getPointColor = (status) => {
    if (status === 'high') return '#d32f2f'; // Red - Above Target
    if (status === 'low') return '#03a9f4'; // Light Blue - Below Target
    return '#1976d2'; // Dark Blue - In-Range
  };

  // Tooltip state
  const [tooltip, setTooltip] = useState(null);
  const chartRef = useRef(null);

  const handlePrint = () => {
    try {
      const el = document.getElementById('bgdc-pdf-content');
      printElement(el, `Blood Glucose Chart - ${patientName}`);
    } catch (e) {
      toast.error(e?.message || 'Print failed');
    }
  };

  const handleExport = async () => {
    const el = document.getElementById('bgdc-pdf-content');
    if (!el) return;
    try {
      await exportElementToPdf(
        el,
        `BG-Chart-${patientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`
      );
      toast.success('Exported to PDF');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  return (
    <div className="bg-day-chart-page">
      <MeasurementChartHeader
        practiceId={practiceId}
        patientId={patientId}
        vitals={patientVitals}
        activeVital="blood-glucose"
        patientName={patientName}
        patientDOB={patientDOB}
        queryType={queryType}
        setQueryType={setQueryType}
        dateRange={dateRange}
        setDateRange={setDateRange}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        onQuery={handleQuery}
        onPrint={handlePrint}
        onExport={handleExport}
        onEmail={() => setShowEmailModal(true)}
        hasData={measurements.length > 0}
      />

      {/* Charts scrollable container */}
      <div className="bgdc-charts-scroll" id="bgdc-pdf-content">
      {/* Day Chart Section */}
      <fieldset className="bgdc-fieldset-custom bgdc-chart-section">
        <legend className="bgdc-legend-custom">Day Chart Of Blood Glucose</legend>
        <div className="bgdc-chart-container">
          <div className="bgdc-chart-title">Standard Day Chart of Blood Glucose</div>
          <div className="bgdc-chart-legend">
            <div className="bgdc-legend-item">
              <div className="bgdc-legend-dot" style={{ backgroundColor: '#d32f2f' }}></div>
              <span>Above Target</span>
            </div>
            <div className="bgdc-legend-item">
              <div className="bgdc-legend-dot" style={{ backgroundColor: '#03a9f4' }}></div>
              <span>Below Target</span>
            </div>
            <div className="bgdc-legend-item">
              <div className="bgdc-legend-dot" style={{ backgroundColor: '#1976d2' }}></div>
              <span>In-Range</span>
            </div>
          </div>
          <div className="bgdc-chart-area">
            {loading || queryLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Loading chart data...</div>
            ) : chartData.dataPoints.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>No data available for the selected period</div>
            ) : (
              <div className="bgdc-chart-placeholder">
                <div className="bgdc-y-axis">
                  {[400, 350, 300, 250, 200, 150, 100, 50, 0].map(value => (
                    <span key={value}>{value}</span>
                  ))}
                </div>
                <div className="bgdc-chart-grid" ref={chartRef}>
                  <svg width={chartWidth} height={chartHeight} style={{ position: 'absolute', top: 0, left: 0 }}>
                    {/* Grid lines */}
                    {[0, 50, 100, 150, 200, 250, 300, 350, 400].map(value => {
                      const y = scaleY(value);
                      if (y == null) return null;
                      return (
                        <line
                          key={value}
                          x1={padding.left}
                          y1={y}
                          x2={chartWidth - padding.right}
                          y2={y}
                          stroke="#e0e0e0"
                          strokeWidth="1"
                        />
                      );
                    })}
                    {/* Data points */}
                    {chartData.dataPoints.map((point, index) => {
                      const x = scaleX(point.timeInMinutes);
                      const y = scaleY(point.value);
                      if (y == null) return null;
                      
                      const color = getPointColor(point.status);
                      
                      const handleMouseEnter = (e) => {
                        const rect = chartRef.current?.getBoundingClientRect();
                        if (!rect) return;
                        
                        setTooltip({
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                          value: point.value,
                          time: formatTime(point.timeInMinutes),
                          status: point.status,
                          points: point.points
                        });
                      };
                      
                      const handleMouseLeave = () => {
                        setTooltip(null);
                      };
                      
                      return (
                        <circle
                          key={`point-${point.timeInMinutes}-${index}`}
                          cx={x}
                          cy={y}
                          r="4"
                          fill={color}
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                        />
                      );
                    })}
                  </svg>
                  <div className="bgdc-x-axis">
                    {/* Show time labels at intervals */}
                    {[0, 6, 12, 18, 24].map(hour => {
                      const timeInMinutes = hour * 60;
                      return (
                        <span key={hour} style={{ left: `${(timeInMinutes / maxTime) * 100}%` }}>
                          {formatTime(timeInMinutes)}
                        </span>
                      );
                    })}
                  </div>
                  {/* Tooltip */}
                  {tooltip && (
                    <div
                      className="bgdc-tooltip"
                      style={{
                        left: `${tooltip.x}px`,
                        top: `${tooltip.y - 80}px`,
                        transform: 'translateX(-50%)'
                      }}
                    >
                      <div className="bgdc-tooltip-content">
                        <div className="bgdc-tooltip-time">{tooltip.time}</div>
                        <div className="bgdc-tooltip-item">
                          <span className="bgdc-tooltip-label">Blood Glucose:</span>
                          <span className="bgdc-tooltip-value">{tooltip.value.toFixed(0)} mg/dL</span>
                        </div>
                        <div className="bgdc-tooltip-item">
                          <span className="bgdc-tooltip-label">Status:</span>
                          <span className="bgdc-tooltip-value">
                            {tooltip.status === 'high' ? 'Above Target' : tooltip.status === 'low' ? 'Below Target' : 'In-Range'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="bgdc-y-axis-label">Blood Glucose mg/dL</div>
        </div>
      </fieldset>

      {/* Pie Chart Section */}
      <fieldset className="bgdc-fieldset-custom bgdc-pie-chart-wrapper">
        <legend className="bgdc-legend-custom">Pie Chart</legend>
        <PieChart practiceId={practiceId} patientId={patientId} />
      </fieldset>

      {/* Mean Chart Section */}
      <fieldset className="bgdc-fieldset-custom bgdc-mean-chart-wrapper">
        <legend className="bgdc-legend-custom">Mean Chart</legend>
        <MeanChart practiceId={practiceId} patientId={patientId} />
      </fieldset>
      </div>{/* end bgdc-charts-scroll */}

      {/* Email Modal */}
      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={async (emailData) => {
          try {
            if (emailData.attachments?.length) {
              toast.info('Attachments are not supported yet. Sending without attachments.');
            }
            const payload = {
              to: emailData.to,
              cc: emailData.cc,
              bcc: emailData.bcc,
              subject: emailData.subject,
              text: emailData.content,
              html: `<pre style="font-family: Arial, sans-serif; white-space: pre-wrap;">${String(emailData.content || '').replace(/</g, '&lt;')}</pre>`,
              event_id: 10
            };
            const res = await emailService.sendCustomNotification(payload);
            if (!res.success) throw new Error(res.error || res.message);
            toast.success('Email queued successfully');
            setShowEmailModal(false);
          } catch (e) {
            toast.error(e?.message || 'Failed to send email');
          }
        }}
        defaultSubject={`Blood Glucose Chart Report - ${new Date().toLocaleDateString()}`}
        defaultContent={`Blood Glucose Chart Report\nPatient: ${patientName} (DOB: ${patientDOB})\nGenerated: ${new Date().toLocaleString()}\nTotal Records: ${measurements.length}`}
      />
    </div>
  );
};

export default DayChart;

